/**
 * Helpers for "Import plants from CSV" on a project's Project Plant List.
 * Goal: forgiving imports — map any spreadsheet headers, parse CSV safely, merge lookup results.
 */

export const PROJECT_CSV_FIELD_ORDER = [
  { key: 'common_name', label: 'Common name (e.g. Lavender)' },
  { key: 'botanical_name', label: 'Scientific name (Latin)' },
  { key: 'quantity', label: 'How many (quantity)' },
  { key: 'pot_size', label: 'Pot / container size' },
  { key: 'color', label: 'Colour' },
  { key: 'unit_price', label: 'Price each' },
  { key: 'width', label: 'Width' },
  { key: 'height', label: 'Height' },
  { key: 'exposure', label: 'Sun / shade' },
  { key: 'plant_type', label: 'Type (shrub, tree, …)' },
  { key: 'code', label: 'Code / SKU' },
  { key: 'notes', label: 'Notes' },
  { key: 'image_url', label: 'Photo link (picture file on the web)' },
  { key: 'garden_app_zone', label: 'Garden App zone (name or zone id)' }
];

export function emptyProjectCsvColumnMapping() {
  return {
    common_name: '',
    botanical_name: '',
    quantity: '',
    pot_size: '',
    color: '',
    unit_price: '',
    width: '',
    height: '',
    exposure: '',
    plant_type: '',
    code: '',
    notes: '',
    image_url: '',
    garden_app_zone: ''
  };
}

function normHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase();
}

/** Guess spreadsheet columns from header text (first row of your file). */
export function suggestProjectCsvColumnMapping(headers) {
  const m = emptyProjectCsvColumnMapping();
  if (!headers || !headers.length) return m;
  const used = new Set();

  const pick = (pred) => {
    for (const h of headers) {
      if (used.has(h)) continue;
      const s = normHeader(h);
      if (!s) continue;
      if (pred(s, h)) {
        used.add(h);
        return h;
      }
    }
    return '';
  };

  m.common_name = pick(
    (s) =>
      s === 'common_name' ||
      s === 'name' ||
      s.includes('common name') ||
      (s.includes('common') && !s.includes('scientific'))
  );
  m.botanical_name = pick(
    (s) =>
      s === 'botanical_name' ||
      s === 'botanical' ||
      s.includes('botanical') ||
      s === 'latin' ||
      s === 'scientific' ||
      s === 'plant' ||
      s.includes('plant name')
  );
  m.quantity = pick(
    (s) => s === 'quantity' || s === 'qty' || s === 'q' || s === '#' || s.includes('how many')
  );
  m.pot_size = pick(
    (s) => s === 'pot_size' || s === 'size' || s.includes('pot size') || s.includes('container')
  );
  m.color = pick((s) => s === 'color' || s === 'colour');
  m.unit_price = pick((s) => s.includes('price') || s === 'unit_price' || s.includes('cost'));
  m.width = pick((s) => s === 'width' || s === 'w');
  m.height = pick((s) => s === 'height' || s === 'h');
  m.exposure = pick((s) => s.includes('sun') || s.includes('shade') || s === 'exposure');
  m.plant_type = pick((s) => s === 'type' || s.includes('plant type'));
  m.code = pick((s) => s === 'code' || s === 'sku' || s.includes('item'));
  m.image_url = pick(
    (s) =>
      (s.includes('image') && !s.includes('google')) ||
      s.includes('photo') ||
      s.includes('picture') ||
      s === 'img'
  );
  m.notes = pick((s) => s === 'notes' || s === 'note' || s.includes('comment'));
  m.garden_app_zone = pick(
    (s) =>
      s === 'garden_app_zone' ||
      s === 'garden zone' ||
      s.includes('garden app zone') ||
      (s.includes('zone') && (s.includes('garden') || s.includes('handoff')))
  );

  return m;
}

function cell(rawRow, headerName) {
  if (!headerName) return '';
  const v = rawRow[headerName];
  return v != null ? String(v).trim() : '';
}

function toInt(v, fallback = 0) {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  if (!s) return fallback;
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? fallback : n;
}

function toNumOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const cleaned = s.replace(/[$,]/g, '');
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? null : n;
}

function looksLikeBotanicalName(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  return /^[A-Z][a-z]+(?:\s+x)?\s+(?:[a-z][a-z-]+|(?:sp|ssp|var)\.)\b/.test(s);
}

/** Turn one spreadsheet row + your column picks into the fields our importer expects. */
export function buildMasterListCsvRow(rawRow, mapping) {
  const g = (k) => cell(rawRow, mapping[k]);

  let common_name = g('common_name');
  let botanical_name = g('botanical_name');
  // One column with Latin-style binomials is often mapped only to "common".
  // Keep ordinary capitalized common names (e.g. "English Lavender") as common names.
  if (!botanical_name && looksLikeBotanicalName(common_name)) {
    botanical_name = common_name;
    common_name = '';
  }

  return {
    ...rawRow,
    common_name: String(common_name || '').trim(),
    botanical_name: String(botanical_name || '').trim(),
    quantity: toInt(g('quantity'), 1) || 1,
    pot_size: g('pot_size'),
    color: g('color'),
    unit_price: toNumOrNull(g('unit_price')),
    width: toNumOrNull(g('width')),
    height: toNumOrNull(g('height')),
    exposure: g('exposure'),
    plant_type: g('plant_type'),
    code: g('code'),
    notes: g('notes'),
    image_url: g('image_url')
  };
}

/** Normalize a library row into the same shape as a public plant for the match picker. */
function libraryItemToMatchOption(it) {
  return {
    id: it.plant_id || null,
    designer_master_id: it.id || null,
    common_name: it.common_name || '',
    botanical_name: it.botanical_name || '',
    image_url: it.image_url || '',
    _matchSource: 'library'
  };
}

function publicPlantToMatchOption(p) {
  return {
    id: p.id,
    designer_master_id: null,
    common_name: p.common_name || '',
    botanical_name: p.botanical_name || '',
    image_url: p.image_url || '',
    _matchSource: 'public'
  };
}

function dedupeMatches(list) {
  const seen = new Set();
  const out = [];
  for (const m of list) {
    const key =
      (m.id && `p:${m.id}`) ||
      (m.designer_master_id && `d:${m.designer_master_id}`) ||
      `n:${(m.common_name || '').toLowerCase()}|${(m.botanical_name || '').toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(m);
  }
  return out.slice(0, 12);
}

/**
 * Look up a plant name in (1) PlantADay's shared plant list and (2) your private library.
 * @param {string} searchQuery
 * @param {string} token auth bearer
 * @param {string} apiBase e.g. '' or 'http://localhost:3001/api'
 */
export async function fetchCombinedPlantMatches(searchQuery, token, apiBase = '') {
  const q = String(searchQuery || '').trim();
  if (q.length < 2) return [];

  const root = apiBase ? `${String(apiBase).replace(/\/$/, '')}/api` : '/api';
  const headers = { Authorization: `Bearer ${token}` };

  const [pubRes, libRes] = await Promise.all([
    fetch(`${root}/plants?search=${encodeURIComponent(q)}&limit=12`, { headers }),
    fetch(`${root}/designer-master-plant-list?search=${encodeURIComponent(q)}&limit=12`, { headers })
  ]);

  let publicPlants = [];
  if (pubRes.ok) {
    const data = await pubRes.json().catch(() => ({}));
    publicPlants = Array.isArray(data.plants) ? data.plants : Array.isArray(data) ? data : [];
  }

  let libItems = [];
  if (libRes.ok) {
    const data = await libRes.json().catch(() => ({}));
    libItems = Array.isArray(data.items) ? data.items : [];
  }

  const merged = [
    ...publicPlants.map(publicPlantToMatchOption),
    ...libItems.map(libraryItemToMatchOption)
  ];
  return dedupeMatches(merged);
}
